// The Sales app's Seller Home: one seller's go-to-market work. What to do next (their open
// follow-ups, worked in place), what they did in the last 7 days, logging a touch by hand, and
// the open leads. Data from GET /api/sales/seller-home (home/sellerHome.ts); follow-up changes
// are PATCHes of the activity record, a logged touch POSTs /api/sales/log-activity. The header's
// Open My Dashboard opens the viewer's favorite reports (/reports/dashboard/favorites; MOTHER-294).
import {
  ActivityTimeline, AttentionList, Button, Card, Combobox, DatePicker, EmptyState, Field, FieldEditor, HomeIcon, HomeLayout, HomeModule,
  InlineMessage, Menu, MenuContent, MenuItem, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger, Modal,
  ModalContent, PageContent, PageHeader, Popover, PopoverContent, PopoverTrigger, SegmentedButtonGroup, Spinner,
  Textarea, TextLink,
  type AttentionItem, type FilterOption, type TimelineEntry,
} from '@olytic/foundation';
import { ArrowUpRight, CalendarDays, Check, Linkedin, ListTodo, Mail, MessageSquare, MoreHorizontal, Phone, StickyNote, UserRound, X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCurrentUser } from '../auth/AuthProvider';
import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { MY_DASHBOARD_ROUTE } from '../navigation';
import { loadLookupOptions } from '../objects/lookupOptions';
import { listPath, recordPath } from '../objects/routes';
import { updateRecord } from '../objects/writes';
import {
  FOLLOW_UP_GROUPS, FOLLOW_UP_TYPES, fetchSellerHome, followUpGroup, followUpMeta, followUpTitle, followUpTypeLabel,
  longDate, quickDates, RECENT_FILTERS, recentByDay, shortDate, touchKind, localStamp,
  type FollowUp, type SellerHome,
} from './sellerHome';
import { stageLabel } from './salesHome';

const LOG_PATH = '/api/sales/log-activity';
const LOG_CHANNELS: readonly { value: string; label: string }[] = [
  { value: 'call', label: 'Call' }, { value: 'email', label: 'Email' }, { value: 'linkedin_dm', label: 'LinkedIn' },
  { value: 'sms', label: 'Text' }, { value: 'meeting', label: 'Meeting' },
];
/** Only active people can own a follow-up (writes.mjs owner()). */
const ACTIVE_PEOPLE = [{ field: 'active', operator: 'equals' as const, value: true }];

const CHANNEL_ICONS: Record<string, ReactNode> = {
  email: <Mail />, linkedin_dm: <Linkedin />, sms: <MessageSquare />, call: <Phone />, meeting: <CalendarDays />, note: <ListTodo />,
};
const channelIcon = (channel: string): ReactNode => CHANNEL_ICONS[channel] ?? <StickyNote />;

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

function RecordLink({ to, children }: { to: string; children: ReactNode }) {
  return <TextLink asChild><Link to={to}>{children}</Link></TextLink>;
}

export function SellerHomePage(): JSX.Element {
  useDocumentTitle('Seller Home');
  const user = useCurrentUser();
  const [data, setData] = useState<SellerHome | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setData(await fetchSellerHome(authenticatedFetch));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorText(e, 'Could not load your Seller Home.'));
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  /** One change to a follow-up, then the page again: a done, dismissed or reassigned one leaves the list. */
  const change = useCallback(async (id: string, body: Record<string, unknown>) => {
    setActionError(null);
    try {
      await updateRecord(authenticatedFetch, 'activities', id, body);
    } catch (e) {
      setActionError(errorText(e, 'Could not save that change.'));
    }
    await reload();
  }, [reload]);

  return (
    <PageContent as="section" width="fill" className="gap-4 px-3 pb-8 pt-5 sm:px-6">
      <Card className="shrink-0 overflow-hidden">
        <PageHeader
          className="border-b-0"
          leading={<HomeIcon className="size-[46px] text-[var(--brand-accent)]" />}
          title="Seller Home"
          subtitle={data ? `${longDateFull(data.today)} · ${user.name}` : user.name}
          actions={(
            <>
              <Button asChild variant="neutral"><Link to={MY_DASHBOARD_ROUTE}>Open My Dashboard</Link></Button>
              <Button asChild variant="brand"><a href="#log-activity">Log Activity</a></Button>
            </>
          )}
        />
      </Card>
      {loadError ? <InlineMessage tone="error" role="alert">{loadError}</InlineMessage> : null}
      {actionError ? <InlineMessage tone="error" role="alert">{actionError}</InlineMessage> : null}
      {data === null && !loadError ? (
        <div className="flex justify-center py-10"><Spinner label="Loading your Seller Home" /></div>
      ) : data ? (
        <HomeLayout
          main={(
            <>
              <UpNext data={data} change={change} />
              <RecentActivity data={data} />
            </>
          )}
          aside={(
            <>
              <LogActivity onLogged={reload} />
              <OpenLeads data={data} />
            </>
          )}
        />
      ) : null}
    </PageContent>
  );
}

/** "Thursday, October 29, 2026". */
function longDateFull(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// ── Up next ────────────────────────────────────────────────────────────────

type Change = (id: string, body: Record<string, unknown>) => Promise<void>;

function UpNext({ data, change }: { data: SellerHome; change: Change }) {
  const [reassigning, setReassigning] = useState<FollowUp | null>(null);
  const items: AttentionItem[] = data.followUps.map((f) => {
    const title = followUpTitle(f);
    const meta = followUpMeta(f);
    return {
      id: f.id,
      group: followUpGroup(f.due_days),
      title: (
        <FieldEditor
          editor={{ kind: 'text', maxLength: 500 }}
          label="Activity Name"
          value={title}
          display={title}
          quiet
          onSave={(value) => change(f.id, { subject: value?.trim() || title })}
        />
      ),
      meta: meta.length ? (
        <>
          {f.person ? <RecordLink to={recordPath(f.person.object, f.person.id)}>{f.person.name}</RecordLink> : null}
          {f.account ? <>{f.person ? ' · ' : ''}<RecordLink to={recordPath('accounts', f.account.id)}>{f.account.name}</RecordLink></> : null}
          {f.opportunity ? <> · <RecordLink to={recordPath('opportunities', f.opportunity.id)}>{f.opportunity.name}</RecordLink></> : null}
        </>
      ) : undefined,
      ...(f.has_draft ? { badge: { label: 'Drafted', tone: 'accent' as const } } : {}),
      iconControl: <TypeMenu followUp={f} change={change} />,
      // ↗ opens the follow-up's record: its plan (the step, the message, why and the research) on top.
      controls: (
        <>
          <Button asChild variant="subtle" size="icon-sm" aria-label={`Open ${title}`} title="Open">
            <Link to={recordPath('activities', f.id)}><ArrowUpRight /></Link>
          </Button>
          <Reschedule followUp={f} today={data.today} change={change} />
          <FollowUpActions followUp={f} change={change} onReassign={() => setReassigning(f)} />
        </>
      ),
    };
  });
  return (
    <>
      <AttentionList
        title="Up Next"
        hideCount
        items={items}
        groups={FOLLOW_UP_GROUPS.map((g) => ({ key: g.key, label: g.label, hideCount: true, ...(g.tone ? { tone: g.tone } : {}) }))}
        emptyTitle="Nothing to follow up on"
        emptyDescription="Follow-ups appear here when you email, message or text someone and they haven’t replied."
        viewAll={{ label: 'All activities', href: listPath('activities') }}
      />
      <ReassignDialog followUp={reassigning} onClose={() => setReassigning(null)} change={change} />
    </>
  );
}

function TypeMenu({ followUp, change }: { followUp: FollowUp; change: Change }) {
  const label = followUpTypeLabel(followUp.channel);
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="subtle" size="icon" aria-label={`Type: ${label}. Change type`}>{channelIcon(followUp.channel)}</Button>
      </MenuTrigger>
      <MenuContent align="start">
        <MenuRadioGroup value={followUp.channel} onValueChange={(channel) => void change(followUp.id, { channel })}>
          {FOLLOW_UP_TYPES.map(([value, text]) => <MenuRadioItem key={value} value={value}>{text}</MenuRadioItem>)}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

function Reschedule({ followUp, today, change }: { followUp: FollowUp; today: string; change: Change }) {
  const [open, setOpen] = useState(false);
  const move = (date: string) => {
    if (!date) return;
    setOpen(false);
    if (date !== followUp.due_on) void change(followUp.id, { due_on: date });
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="subtle" size="sm" aria-label={`Due ${longDate(followUp.due_on)}. Reschedule`} title={longDate(followUp.due_on)}>
          {shortDate(followUp.due_on)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-56 flex-col gap-1 p-2">
        {quickDates(today).map((q) => (
          <Button key={q.label} variant="subtle" size="sm" className="justify-between" onClick={() => move(q.date)}>
            <span>{q.label}</span><span>{longDate(q.date)}</span>
          </Button>
        ))}
        <Field label="Pick a Date">
          <DatePicker value={followUp.due_on} onChange={() => {}} onPick={move} />
        </Field>
      </PopoverContent>
    </Popover>
  );
}

function FollowUpActions({ followUp, change, onReassign }: { followUp: FollowUp; change: Change; onReassign: () => void }) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="subtle" size="icon-sm" aria-label="Actions"><MoreHorizontal /></Button>
      </MenuTrigger>
      <MenuContent align="end">
        <MenuItem asChild><Link to={recordPath('activities', followUp.id)}><ArrowUpRight aria-hidden /> Open</Link></MenuItem>
        <MenuItem onSelect={() => void change(followUp.id, { status: 'done' })}><Check aria-hidden /> Mark Done</MenuItem>
        <MenuItem onSelect={() => void change(followUp.id, { status: 'dismissed' })}><X aria-hidden /> Dismiss</MenuItem>
        <MenuSeparator />
        <MenuItem onSelect={onReassign}><UserRound aria-hidden /> Reassign…</MenuItem>
      </MenuContent>
    </Menu>
  );
}

function ReassignDialog({ followUp, onClose, change }: { followUp: FollowUp | null; onClose: () => void; change: Change }) {
  return (
    <Modal open={followUp !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <ModalContent title="Reassign Follow-Up" description={followUp ? followUpTitle(followUp) : undefined}>
        {followUp ? (
          <Field label="Assign To">
            <Combobox
              aria-label="Assign To"
              placeholder="Search people"
              autoFocus
              loadOptions={loadLookupOptions('people', ACTIVE_PEOPLE)}
              onSelect={(option) => {
                if (!option) return;
                onClose();
                void change(followUp.id, { owner_id: option.value });
              }}
            />
          </Field>
        ) : null}
      </ModalContent>
    </Modal>
  );
}

// ── Recent activity ────────────────────────────────────────────────────────

function RecentActivity({ data }: { data: SellerHome }) {
  const [filter, setFilter] = useState('all');
  // The same timeline as an opportunity's or contact's Activity tab, newest first.
  const touches = recentByDay(data.recent, filter).flatMap((day) => day.touches);
  const entries: TimelineEntry[] = touches.map((t) => {
    const kind = touchKind(t.channel, t.direction);
    return {
      id: t.id,
      title: <RecordLink to={recordPath('activities', t.id)}>{t.subject?.trim() || kind}</RecordLink>,
      body: (
        <>
          {kind}
          {t.people.map((p) => <span key={`${p.object}:${p.id}`}> · <RecordLink to={recordPath(p.object, p.id)}>{p.name}</RecordLink></span>)}
          {t.account ? <> · <RecordLink to={recordPath('accounts', t.account.id)}>{t.account.name}</RecordLink></> : null}
        </>
      ),
      timestamp: localStamp(t.occurred_at),
      icon: channelIcon(t.channel),
    };
  });
  return (
    <HomeModule
      title="Recent Activity"
      viewAll={{ label: 'All activities', href: listPath('activities') }}
      actions={(
        <SegmentedButtonGroup
          size="sm"
          label="Channel"
          value={filter}
          onChange={setFilter}
          options={RECENT_FILTERS.map(([value, label]) => ({ value, label }))}
        />
      )}
    >
      <div className="px-4 py-3">
        {entries.length ? (
          <ActivityTimeline datePlacement="leading" entries={entries} />
        ) : (
          <EmptyState title={filter === 'all' ? 'Nothing logged in the last 7 days' : 'Nothing on this channel in the last 7 days'} />
        )}
      </div>
    </HomeModule>
  );
}

// ── Log activity ───────────────────────────────────────────────────────────

/** Contacts and leads together for "With", each value carrying its object. */
async function loadPeopleAndLeads(query: string): Promise<FilterOption[]> {
  const [contacts, leads] = await Promise.all([loadLookupOptions('contacts')(query), loadLookupOptions('leads')(query)]);
  return [
    ...contacts.map((o) => ({ ...o, value: `contacts:${o.value}` })),
    ...leads.map((o) => ({ ...o, value: `leads:${o.value}`, label: `${o.label} (Lead)` })),
  ];
}

function LogActivity({ onLogged }: { onLogged: () => Promise<void> }) {
  const [channel, setChannel] = useState('call');
  const [withWhom, setWithWhom] = useState<FilterOption | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [formKey, setFormKey] = useState(0);

  const submit = async () => {
    if (!withWhom) {
      setMessage({ tone: 'error', text: 'Choose who it was with.' });
      return;
    }
    if (notes.trim() === '') {
      setMessage({ tone: 'error', text: 'Add a line about what happened.' });
      return;
    }
    const [object, id] = withWhom.value.split(':');
    setSaving(true);
    setMessage(null);
    try {
      const response = await authenticatedFetch(LOG_PATH, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ object, record_id: id, channel, summary: notes }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Could not log it (${response.status}).`);
      }
      setMessage({ tone: 'success', text: `Logged with ${withWhom.label}.` });
      setWithWhom(null);
      setNotes('');
      setFormKey((k) => k + 1);
      await onLogged();
    } catch (e) {
      setMessage({ tone: 'error', text: errorText(e, 'Could not log it.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="log-activity">
      <HomeModule title="Log Activity">
        <form
          className="flex flex-col gap-3 px-4 py-3"
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
        >
          <SegmentedButtonGroup size="sm" label="Channel" value={channel} onChange={setChannel} options={LOG_CHANNELS} />
          <Field label="With" required>
            <Combobox
              key={formKey}
              aria-label="With"
              placeholder="Contact or lead"
              loadOptions={loadPeopleAndLeads}
              value={withWhom?.value ?? null}
              {...(withWhom ? { selectedLabel: withWhom.label } : {})}
              onSelect={setWithWhom}
            />
          </Field>
          <Field label="Notes" required>
            <Textarea rows={3} placeholder="What happened, and what’s next" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          {message ? <InlineMessage tone={message.tone} role={message.tone === 'error' ? 'alert' : 'status'}>{message.text}</InlineMessage> : null}
          <div className="flex justify-end">
            <Button type="submit" variant="brand" disabled={saving}>{saving ? 'Logging…' : 'Log It'}</Button>
          </div>
        </form>
      </HomeModule>
    </div>
  );
}

// ── Open leads ─────────────────────────────────────────────────────────────

function OpenLeads({ data }: { data: SellerHome }) {
  const items: AttentionItem[] = data.leads.map((l) => ({
    id: l.id,
    title: <RecordLink to={recordPath('leads', l.id)}>{l.name || 'Unnamed lead'}</RecordLink>,
    meta: [l.company_name, l.source ? `From ${l.source}` : null, `Added ${longDate(l.created_on)}`].filter(Boolean).join(' · '),
    ...(l.status ? { badge: { label: stageLabel(l.status), tone: 'warning' as const } } : {}),
  }));
  return (
    <AttentionList
      title="Open Leads"
      hideCount
      items={items}
      emptyTitle="No open leads"
      viewAll={{ label: 'All leads', href: listPath('leads') }}
    />
  );
}
